import styled, { css } from 'styled-components';
import { rgba } from 'polished';
import IconButton from '@components/1-atoms/IconButton';
import Text, { TEXT_VARIANT_BODY_SMALL } from '@components/1-atoms/Text';
import { SIDEBAR_INSET, SIDEBAR_SINGLE_SCROLL_MEDIA } from '@constants/sidebar';
import {
  NavigationItemReactIcon,
  NavigationItemLabel,
  NavigationItem,
} from '@components/2-molecules/NavigationDrawer/NavigationDrawer.styled';

export { RouterLink } from '@components/2-molecules/NavigationDrawer/NavigationDrawer.styled';

export const ConversationItem = styled(NavigationItem)`
  flex: 1;
  overflow: hidden;
  min-width: 0;
  gap: 9px;
  padding-left: 14px;
`;

export const ConversationReactIcon = styled(NavigationItemReactIcon)`
  width: 22px;
  height: 22px;
`;

export const ConversationTitle = styled(NavigationItemLabel)`
  min-width: 0;
  flex: 1;
  padding-right: 32px;
  font-weight: 500;
  color: ${({ theme: { colors } }) => colors.onSurfaceVariant};
`;

/**
 * Off-screen but still in the accessibility tree — `clip` rather than
 * `display: none` so screen readers keep reading it.
 */
export const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  ${SIDEBAR_SINGLE_SCROLL_MEDIA} {
    flex: none;
    overflow: visible;
  }
`;

export const Header = styled.button`
  display: flex;
  align-items: center;
  align-self: flex-start;
  flex-shrink: 0;
  gap: 4px;
  margin: 13px 0 0 31px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
`;

export const HeaderTitle = styled.span`
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme: { colors } }) => colors.onSurfaceVariant};
`;

export const HeaderChevron = styled.span<{ $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  transition: transform 0.2s ease;
  transform: rotate(${({ $isCollapsed }) => ($isCollapsed ? '-90deg' : '0deg')});
  color: ${({ theme: { colors } }) => rgba(colors.onSurfaceVariant, 0.7)};
`;

export const ListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  scrollbar-gutter: stable;
  padding: 16px 0 8px ${SIDEBAR_INSET}px;
  margin-right: 14px;
  scrollbar-color: ${({ theme: { colors } }) => colors.shellDivider} transparent;

  /* The drawer scrolls instead; ConversationList points the virtualiser at it. */
  ${SIDEBAR_SINGLE_SCROLL_MEDIA} {
    flex: none;
    overflow-y: visible;
  }
`;

export const VirtualListInner = styled.div<{ $height: number }>`
  width: 100%;
  position: relative;
  height: ${({ $height }) => `${$height}px`};
`;

export const VirtualRow = styled.div<{ $height: number; $offset: number }>`
  position: absolute;
  left: 0;
  width: 100%;
  height: ${({ $height }) => `${$height}px`};
  top: ${({ $offset }) => `${$offset}px`};
`;

export const ConversationItemWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  min-width: 0;

  ${({ theme: { borderRadius } }) => css`
    border-radius: ${borderRadius.full};
  `}
`;

export const MenuButton = styled(IconButton)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;

  ${({ theme: { spacing } }) => css`
    right: ${spacing.level2};
  `}

  ${ConversationItemWrapper}:hover &,
  ${ConversationItemWrapper}:focus-within & {
    opacity: 0.7;
    pointer-events: auto;
  }

  ${ConversationItemWrapper}:hover &:hover,
  &:focus-visible {
    opacity: 1;
    pointer-events: auto;
  }

  ${ConversationItemWrapper}[data-menu-open='true'] & {
    opacity: 1;
    pointer-events: auto;
  }

  /* Touch devices: always visible so the first tap navigates, not wasted on an invisible button */
  @media (hover: none) {
    opacity: 0.5;
    pointer-events: auto;
  }
`;

export const MenuOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999;
`;

export const Menu = styled.div<{ $openAbove?: boolean }>`
  position: absolute;
  right: 0;
  z-index: 1000;
  min-width: 160px;
  ${({ $openAbove }) =>
    $openAbove
      ? css`
          bottom: 100%;
        `
      : css`
          top: 100%;
        `}

  ${({ theme: { colors, borderRadius, spacing } }) => css`
    border-radius: ${borderRadius.small};
    background-color: ${colors.surfaceContainerHigh};
    padding: ${spacing.level3} 0;
    box-shadow:
      0px 1px 2px 0px ${rgba(colors.shadow, 0.3)},
      0px 2px 6px 2px ${rgba(colors.shadow, 0.15)};
  `}
`;

export const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ theme: { colors, spacing } }) => css`
    padding: ${spacing.level6} ${spacing.level5};
    color: ${colors.onSurfaceVariant};
  `}
`;

export const EmptyText = styled(Text).attrs({
  variant: TEXT_VARIANT_BODY_SMALL,
})`
  ${({ theme: { colors } }) => css`
    color: ${colors.onSurfaceVariant};
  `}
`;

export const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ theme: { spacing } }) => css`
    padding: ${spacing.level5};
  `}
`;

export const LoadingMore = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ theme: { colors, spacing, font } }) => css`
    padding: ${spacing.level3};
    font-size: ${font.bodySmall.fontSize};
    color: ${colors.onSurfaceVariant};
  `}
`;
